import { css } from '@emotion/react';
import dayjs from 'dayjs';
import { Trans } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { Button, currency, SpinningLoader, Text } from '@frontend/design-system';
import { useCareCreditTranslations } from '../hooks/use-care-credit-translations';
import { CareCreditPatientData } from '../types/care-credit.types';
import { CareCreditChip } from './care-credit-chip';

const styles = {
  item: css`
    position: relative;
    padding: ${theme.spacing(2)};
    border-radius: ${theme.borderRadius.medium};
    background: ${theme.colors.white};
    box-shadow: ${theme.shadows.light};
  `,
  topRow: css`
    display: flex;
    gap: ${theme.spacing(2)};
    align-items: center;
    justify-content: space-between;
    margin-bottom: ${theme.spacing(1)};
  `,
  brandLine: css`
    display: flex;
    align-items: center;
    line-height: 1;
    gap: ${theme.spacing(1)};
    font-size: ${theme.font.size.large};
  `,
  loadingContainer: css`
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.5);
  `,
  spinner: css`
    opacity: 0.5;
  `,
  subText: css`
    color: ${theme.colors.neutral50};
    font-weight: 400;
  `,
  unknownStatusBox: css`
    background: ${theme.colors.neutral5};
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    padding: ${theme.spacing(2)};
    border-radius: ${theme.borderRadius.medium};
    gap: ${theme.spacing(1)};
    color: ${theme.colors.neutral70};

    svg {
      color: ${theme.colors.neutral60};
    }
  `,
};

export type CareCreditPersonStatusItemProps = {
  className?: string;
  careCreditData?: CareCreditPatientData;
  isLoading?: boolean;
  onClickPrequalify?: () => void;
};
export const CareCreditPersonStatusItem = ({
  careCreditData,
  className,
  isLoading,
  onClickPrequalify,
}: CareCreditPersonStatusItemProps) => {
  const { t } = useCareCreditTranslations();

  const { status, amount, asOfDate } = careCreditData || {};

  let bottomRowJsx: React.ReactNode = null;
  let chipJsx: React.ReactNode = null;

  const formattedAmount = amount ? currency(amount, { decimals: 0 }) : 'N/A';
  const date = asOfDate ? dayjs(asOfDate).format('MMM DD, YYYY') : null;

  if (!isLoading) {
    if (!careCreditData) {
      bottomRowJsx = (
        <div css={styles.unknownStatusBox}>
          <div>
            <Icon name='help' size={16} />
          </div>
          <div>
            <Text size='medium'>{t('Unable to retrieve CareCredit eligibility status at this time')}</Text>
          </div>
        </div>
      );
    } else if (status === 'approved') {
      bottomRowJsx = (
        <Text size='small'>
          <Trans>
            Available: <b>{formattedAmount}</b> as of {date}
          </Trans>
        </Text>
      );
    } else if (status === 'pre-approved') {
      bottomRowJsx = (
        <Text size='small'>
          <Trans>
            Credit Limit: <b>{formattedAmount}</b>
            <br />
            <i css={styles.subText}>(Subject to approval)</i>
          </Trans>
        </Text>
      );
    } else if (status === 'not-enrolled') {
      bottomRowJsx = (
        <Button variant='secondary' onClick={onClickPrequalify} disabled={!onClickPrequalify}>
          {t('Check Now')}
        </Button>
      );
    }

    if (status) {
      chipJsx = <CareCreditChip status={status} />;
    }
  }

  return (
    <div className={`care-credit-person-status-item ${className || ''}`} css={styles.item}>
      <div className='top-row' css={styles.topRow}>
        <div>
          <Text size='medium' weight='bold' css={styles.brandLine}>
            <Icon name='care-credit' height={16} width={16} />
            {t('CareCredit')}
          </Text>
        </div>
        <div>{chipJsx}</div>
      </div>

      <div className='bottom-row'>{bottomRowJsx}</div>

      {isLoading && (
        <div css={styles.loadingContainer}>
          <SpinningLoader size='xs' css={styles.spinner} />
        </div>
      )}
    </div>
  );
};
